import {Fragment} from 'react';
import {Button} from '@ui/buttons/button';
import {CheckIcon} from '@ui/icons/material/Check';
import {FullPageLoader} from '@ui/progress/full-page-loader';
import {StaticPageTitle} from '@common/seo/static-page-title';
import {Trans} from '@ui/i18n/trans';
import {Product} from '@common/billing/product';
import {useProducts} from '@common/billing/pricing-table/use-products';
import {useSettings} from '@ui/settings/use-settings';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {Dialog} from '@ui/overlays/dialog/dialog';
import {DialogBody} from '@ui/overlays/dialog/dialog-body';
import {useDialogContext} from '@ui/overlays/dialog/dialog-context';

export function AppInfoPlansPage() {
  const {android} = useSettings();
  const query = useProducts();
  const plans = (query.data?.products || []).filter(
    plan => !plan.hidden && !plan.free,
  );
  const externalPlanLinkEnabled = Boolean(
    android?.external_plan_link_enabled,
  );
  const pricingUrl = 'https://vibe.sintonizy.com/pricing';
  const externalPricingHref = `sintonizy-external://open?url=${encodeURIComponent(
    pricingUrl,
  )}`;

  return (
    <Fragment>
      <StaticPageTitle>
        <Trans message="Sintonizy plans" />
      </StaticPageTitle>
      <main className="min-h-screen bg-black px-20 pb-56 pt-40 text-white sm:px-32">
        <section className="mx-auto flex w-full max-w-950 flex-col gap-30">
          <header className="space-y-12">
            <div className="text-sm font-bold uppercase tracking-[0.24em] text-primary">
              <Trans message="Sintonizy Premium" />
            </div>
            <h1 className="max-w-680 text-4xl font-black leading-tight sm:text-5xl">
              <Trans message="Get more from your music." />
            </h1>
            <p className="max-w-620 text-base leading-7 text-gray-300">
              <Trans message="Compare the benefits available on Sintonizy plans. Subscription changes are completed on the web." />
            </p>
          </header>

          <section className="rounded-2xl border border-white/10 bg-white/5 p-22">
            <h2 className="mb-16 text-xl font-bold">
              <Trans message="Why upgrade?" />
            </h2>
            <div className="grid gap-14 sm:grid-cols-2">
              {globalBenefits.map(benefit => (
                <BenefitItem key={benefit} label={benefit} />
              ))}
            </div>
          </section>

          {query.isLoading ? (
            <FullPageLoader className="min-h-320 text-white" />
          ) : (
            <section className="grid gap-18 sm:grid-cols-2 lg:grid-cols-3">
              {plans.map(plan => (
                <PlanInfoCard
                  key={plan.id}
                  product={plan}
                  externalPlanLinkEnabled={externalPlanLinkEnabled}
                  externalPricingHref={externalPricingHref}
                />
              ))}
            </section>
          )}

          {!externalPlanLinkEnabled && (
            <p className="rounded-xl border border-white/10 bg-white/5 px-18 py-14 text-sm text-gray-300">
              <Trans message="Plan signup is currently available on the Sintonizy website." />
            </p>
          )}
        </section>
      </main>
    </Fragment>
  );
}

interface PlanInfoCardProps {
  product: Product;
  externalPlanLinkEnabled: boolean;
  externalPricingHref: string;
}

function PlanInfoCard({
  product,
  externalPlanLinkEnabled,
  externalPricingHref,
}: PlanInfoCardProps) {
  const copy = getPlanCopy(product);

  return (
    <article className="flex min-h-360 flex-col rounded-2xl border border-white/10 bg-[#191919] p-22 shadow-lg">
      <div className="mb-18">
        <div className="mb-10 text-xs font-bold uppercase tracking-[0.2em] text-white/60">
          <Trans message="Plan" />
        </div>
        <h2 className="text-3xl font-black leading-tight text-white">
          <Trans message={product.name} />
        </h2>
        <p className="mt-10 min-h-48 text-sm leading-6 text-gray-300">
          <Trans message={copy.description} />
        </p>
      </div>

      <div className="mb-24 flex flex-1 flex-col gap-12">
        {copy.benefits.map(benefit => (
          <BenefitItem key={benefit} label={benefit} />
        ))}
      </div>

      <DialogTrigger type="tray">
        <Button
          variant={externalPlanLinkEnabled ? 'flat' : 'outline'}
          color="primary"
          radius="rounded-full"
          size="md"
          className={
            externalPlanLinkEnabled
              ? 'w-full text-base font-black'
              : 'w-full border-white/20 text-base font-black text-white/70'
          }
        >
          <Trans
            message={externalPlanLinkEnabled ? 'Start Now' : 'Available on web'}
          />
        </Button>

        <SubscriptionOutsideAppSheet
          externalPlanLinkEnabled={externalPlanLinkEnabled}
          externalPricingHref={externalPricingHref}
        />
      </DialogTrigger>
    </article>
  );
}

interface SubscriptionOutsideAppSheetProps {
  externalPlanLinkEnabled: boolean;
  externalPricingHref: string;
}

function SubscriptionOutsideAppSheet({
  externalPlanLinkEnabled,
  externalPricingHref,
}: SubscriptionOutsideAppSheetProps) {
  const {close} = useDialogContext();

  const handleGotIt = () => {
    close();

    if (externalPlanLinkEnabled) {
      window.location.href = externalPricingHref;
    }
  };

  return (
    <Dialog
      size="w-full"
      background="bg-[#202020] text-white"
      radius="rounded-t-3xl"
      maxHeight="max-h-tray"
      shadow="shadow-2xl"
      className="border-white/10"
    >
      <DialogBody padding="px-30 pb-32 pt-18">
        <div className="mx-auto mb-28 h-5 w-48 rounded-full bg-white/30" />
        <div className="mb-26 flex items-center justify-center gap-10">
          <img
            src="/images/logo-light.svg"
            alt=""
            className="h-22 w-auto max-w-[150px]"
          />
          <span className="text-base font-black text-primary">
            <Trans message="Premium" />
          </span>
        </div>
        <h2 className="mx-auto max-w-320 text-center text-2xl font-black leading-tight">
          <Trans message="Subscription changes take place outside the app." />
        </h2>
        <p className="mx-auto mt-18 max-w-320 text-center text-base leading-7 text-gray-300">
          <Trans message="All subscription changes are made through your Sintonizy account, outside the app." />
        </p>
        <Button
          variant="flat"
          color="primary"
          radius="rounded-full"
          size="lg"
          className="mx-auto mt-30 min-w-180 text-base font-black"
          onClick={handleGotIt}
        >
          <Trans message="Got it" />
        </Button>
      </DialogBody>
    </Dialog>
  );
}

interface BenefitItemProps {
  label: string;
}

function BenefitItem({label}: BenefitItemProps) {
  return (
    <div className="flex items-start gap-10 text-sm leading-6 text-gray-100">
      <CheckIcon size="sm" className="mt-2 flex-shrink-0 text-primary" />
      <span>
        <Trans message={label} />
      </span>
    </div>
  );
}

function getPlanCopy(product: Product): {description: string; benefits: string[]} {
  const name = product.name.toLowerCase();

  if (name.includes('family') || name.includes('famil')) {
    return {
      description: 'A premium experience for families and shared households.',
      benefits: [
        'Family-friendly access for multiple listeners.',
        'Premium listening benefits for each member.',
        'Offline listening for saved music.',
        'Cancel whenever you need.',
      ],
    };
  }

  if (name.includes('student') || name.includes('studant')) {
    return {
      description: 'A lighter premium plan made for eligible students.',
      benefits: [
        'Premium access for one student account.',
        'Ad-free music listening.',
        'Offline listening for saved music.',
        'Cancel whenever you need.',
      ],
    };
  }

  if (name.includes('vip') || name.includes('premium')) {
    return {
      description: 'The main Sintonizy premium plan for everyday listening.',
      benefits: [
        'Ad-free music listening.',
        'Offline listening for saved music.',
        'High-quality audio experience.',
        'Exclusive profile benefits.',
      ],
    };
  }

  return {
    description: 'A Sintonizy membership plan with premium listening benefits.',
    benefits: [
      'Ad-free music listening.',
      'Offline listening for saved music.',
      'High-quality audio experience.',
      'Cancel whenever you need.',
    ],
  };
}

const globalBenefits = [
  'Listen without interruptions.',
  'Save music for offline playback.',
  'Enjoy a cleaner premium experience.',
  'Manage your subscription on the web.',
];
